import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle } from "docx"

export const isHtmlContent = (content: string | undefined) => {
    if (!content) {
        return false
    }

    try {
        const trimmedText = content.trim().toLowerCase()

        const htmlPatterns = [/^<!doctype\s+html/i, /^<html/i, /<html[\s>]/i, /<head[\s>]/i, /<body[\s>]/i, /<!DOCTYPE\s+html/i]

        return htmlPatterns.some((pattern) => pattern.test(trimmedText)) || (trimmedText.includes("<") && trimmedText.includes(">") && (trimmedText.includes("<html") || trimmedText.includes("<head") || trimmedText.includes("<body") || trimmedText.includes("<div")))
    } catch (error) {
        console.error("Error reading file content:", error)
        return false
    }
}

export const markdownToDocx = (text: string) => {
    const processedContent = convertLinks(processContentForMarkdown(text)) as string
    const paragraphs = parseMarkdownToDocx(processedContent)

    return new Document({
        sections: [
            {
                properties: {},
                children: paragraphs,
            },
        ],
        numbering: {
            config: [
                {
                    reference: "default-numbering",
                    levels: [
                        {
                            level: 0,
                            format: "bullet",
                            text: "•",
                            alignment: AlignmentType.LEFT,
                            style: {
                                paragraph: {
                                    indent: { left: 432, hanging: 216 },
                                },
                            },
                        },
                        {
                            level: 1,
                            format: "bullet",
                            text: "◦",
                            alignment: AlignmentType.LEFT,
                            style: {
                                paragraph: {
                                    indent: { left: 792, hanging: 216 },
                                },
                            },
                        },
                        {
                            level: 2,
                            format: "bullet",
                            text: "▪",
                            alignment: AlignmentType.LEFT,
                            style: {
                                paragraph: {
                                    indent: { left: 1152, hanging: 216 },
                                },
                            },
                        },
                    ],
                },
            ],
        },
        styles: {
            default: {
                heading1: {
                    run: {
                        size: 32,
                        bold: true,
                        color: "111827",
                    },
                    paragraph: {
                        spacing: { before: 240, after: 120 },
                    },
                },
                heading2: {
                    run: {
                        size: 28,
                        bold: true,
                        color: "111827",
                    },
                    paragraph: {
                        spacing: { before: 200, after: 100 },
                    },
                },
                heading3: {
                    run: {
                        size: 24,
                        bold: true,
                        color: "111827",
                    },
                    paragraph: {
                        spacing: { before: 160, after: 80 },
                    },
                },
            },
        },
    })
}

const processContentForMarkdown = (content: string | object) => {
    if (typeof content !== "string") {
        return content as unknown as string
    }

    if (/\d+\.\s+.+/.test(content) && !content.includes("\n\n")) {
        return content.replace(/(\d+\.\s+.+?)(?=\s+\d+\.\s+|$)/g, "$1\n\n")
    }

    return content
}

const convertLinks = (content: string | object) => {
    if (typeof content !== "string") {
        return content as unknown as string
    }

    return content.replace(/\[([^\]]+)\]\(store:([a-f\d]{24})\)/gi, (_, text, id) => {
        return `[${text}](/store/objects/${id})`
    })
}

const parseMarkdownToDocx = (markdown: string) => {
    const cleanedText = markdown.replace(/<!--.*?-->/gs, "")
    const lines = cleanedText.split("\n")
    const paragraphs: any[] = []
    let i = 0

    while (i < lines.length) {
        const line = lines[i].trim()

        // Skip empty lines
        if (!line) {
            i++
            continue
        }

        // Handle tables
        if (line.startsWith("|") && i + 1 < lines.length && lines[i + 1].trim().startsWith("|")) {
            const tableRows = []
            let rowIndex = i

            while (rowIndex < lines.length && lines[rowIndex].trim().startsWith("|")) {
                const row = lines[rowIndex].trim()
                if (!row.startsWith("|---")) {
                    const cells = row
                        .split("|")
                        .slice(1, -1)
                        .map((cell) => cell.trim())

                    tableRows.push(
                        new TableRow({
                            children: cells.map(
                                (cellText) =>
                                    new TableCell({
                                        children: [
                                            new Paragraph({
                                                children: parseInlineMarkdown(cellText, 24),
                                            }),
                                        ],
                                        borders: {
                                            top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                            left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                            right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                        },
                                    })
                            ),
                        })
                    )
                }
                rowIndex++
            }

            if (tableRows.length > 0) {
                paragraphs.push(
                    new Table({
                        rows: tableRows,
                        width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                        },
                    })
                )
            }

            i = rowIndex
            continue
        }

        // Headers
        if (line.startsWith("# ")) {
            paragraphs.push(
                new Paragraph({
                    text: line.substring(2),
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 240, after: 120 },
                })
            )
        } else if (line.startsWith("## ")) {
            paragraphs.push(
                new Paragraph({
                    text: line.substring(3),
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 200, after: 100 },
                })
            )
        } else if (line.startsWith("### ")) {
            paragraphs.push(
                new Paragraph({
                    text: line.substring(4),
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 160, after: 80 },
                })
            )
        } else if (line.startsWith("> ")) {
            // Blockquotes
            const blockquoteText = line.substring(2)
            paragraphs.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: blockquoteText,
                            italics: true,
                            color: "555555",
                        }),
                    ],
                    indent: { left: 720 }, // Indent like border-left
                    spacing: { before: 120, after: 120 },
                })
            )
        } else if (line.startsWith("```")) {
            // Code blocks
            const language = line.substring(3)
            i++
            let codeContent = ""

            while (i < lines.length && !lines[i].trim().startsWith("```")) {
                codeContent += lines[i] + "\n"
                i++
            }

            if (language) {
                paragraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: language,
                                font: "Consolas",
                                size: 18,
                                color: "E5E7EB",
                            }),
                        ],
                        shading: {
                            type: "solid",
                            color: "374151",
                        },
                        spacing: { before: 80 },
                    })
                )
            }

            paragraphs.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: codeContent.trim(),
                            font: "Consolas",
                            size: 20,
                            color: "F3F4F6",
                        }),
                    ],
                    shading: {
                        type: "solid",
                        color: "1F2937",
                    },
                    spacing: { after: 80 },
                })
            )
        } else if (line === "---" || line === "***") {
            // Horizontal rules
            paragraphs.push(
                new Paragraph({
                    children: [new TextRun({ text: "" })],
                    border: {
                        bottom: {
                            color: "D1D5DB",
                            space: 1,
                            style: BorderStyle.SINGLE,
                            size: 6,
                        },
                    },
                    spacing: { before: 160, after: 160 },
                })
            )
        } else if (line.startsWith("- ") || line.startsWith("* ") || line.startsWith("  - ") || line.startsWith("    - ") || line.startsWith("• ") || line.startsWith("  • ")) {
            // Unordered lists (handle both - and * with proper indentation)
            const listItems = []
            while (i < lines.length) {
                const currentLine = lines[i]
                const trimmedLine = currentLine.trim()

                // Check if this line is a list item (handles various indentations and bullet types)
                if (trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ") || trimmedLine.startsWith("• ")) {
                    // Determine indentation level
                    const leadingSpaces = currentLine.length - currentLine.trimStart().length
                    const indentLevel = Math.floor(leadingSpaces / 2) // 2 spaces = 1 indent level

                    let itemText = ""
                    if (trimmedLine.startsWith("- ")) {
                        itemText = trimmedLine.substring(2)
                    } else if (trimmedLine.startsWith("* ")) {
                        itemText = trimmedLine.substring(2)
                    } else if (trimmedLine.startsWith("• ")) {
                        itemText = trimmedLine.substring(2)
                    }

                    listItems.push({ text: itemText, indent: indentLevel })
                    i++
                } else if (trimmedLine === "" && i + 1 < lines.length && (lines[i + 1].trim().startsWith("- ") || lines[i + 1].trim().startsWith("* ") || lines[i + 1].trim().startsWith("• "))) {
                    // Skip empty lines between list items
                    i++
                } else {
                    // Not a list item, break out of list parsing
                    break
                }
            }
            i-- // Adjust for the outer loop increment

            listItems.forEach(({ text, indent }) => {
                const leftIndent = 432 + indent * 360 // Base indent + additional for each level

                paragraphs.push(
                    new Paragraph({
                        children: parseInlineMarkdown(text, 22),
                        bullet: {
                            level: indent,
                        },
                        indent: { left: leftIndent },
                        spacing: { before: 40, after: 40 },
                    })
                )
            })
        } else if (/^\d+\.\s/.test(line) || /^\s+\d+\.\s/.test(line)) {
            // Ordered lists (handle nested numbering)
            const listItems = []
            while (i < lines.length) {
                const currentLine = lines[i]
                const trimmedLine = currentLine.trim()

                if (/^\d+\.\s/.test(trimmedLine)) {
                    // Determine indentation level
                    const leadingSpaces = currentLine.length - currentLine.trimStart().length
                    const indentLevel = Math.floor(leadingSpaces / 2)

                    const itemText = trimmedLine.replace(/^\d+\.\s/, "")
                    listItems.push({ text: itemText, indent: indentLevel })
                    i++
                } else if (trimmedLine === "" && i + 1 < lines.length && /^\s*\d+\.\s/.test(lines[i + 1])) {
                    // Skip empty lines between list items
                    i++
                } else {
                    break
                }
            }
            i-- // Adjust for the outer loop increment

            const counters = [0, 0, 0, 0] // Support up to 4 levels
            listItems.forEach(({ text, indent }) => {
                // Reset deeper level counters when going back to a shallower level
                for (let j = indent + 1; j < counters.length; j++) {
                    counters[j] = 0
                }
                counters[indent]++

                const leftIndent = 432 + indent * 360

                paragraphs.push(
                    new Paragraph({
                        children: parseInlineMarkdown(text, 22),
                        numbering: {
                            reference: "default-numbering",
                            level: indent,
                        },
                        indent: { left: leftIndent },
                        spacing: { before: 40, after: 40 },
                    })
                )
            })
        } else {
            // Regular paragraphs
            const textRuns = parseInlineMarkdown(line)
            paragraphs.push(
                new Paragraph({
                    children: textRuns,
                    spacing: { before: 80, after: 80 },
                })
            )
        }

        i++
    }

    return paragraphs
}

const parseInlineMarkdown = (text: string, fontSize: number = 22): TextRun[] => {
    const runs: TextRun[] = []
    let currentIndex = 0

    // Parse bold, italic, and inline code
    const patterns = [
        { regex: /\*\*(.*?)\*\*/g, style: { bold: true } },
        { regex: /\*(.*?)\*/g, style: { italics: true } },
        { regex: /`(.*?)`/g, style: { font: "Consolas", shading: { type: "solid", color: "F3F4F6" } as unknown } },
    ]

    const matches: Array<{ match: RegExpExecArray; style: unknown; startIndex: number; endIndex: number }> = []

    patterns.forEach((pattern) => {
        let match
        const regex = new RegExp(pattern.regex.source, "g")
        while ((match = regex.exec(text)) !== null) {
            matches.push({
                match,
                style: pattern.style,
                startIndex: match.index!,
                endIndex: match.index! + match[0].length,
            })
        }
    })

    // Sort matches by position and remove overlapping matches
    matches.sort((a, b) => a.startIndex - b.startIndex)

    // Filter out overlapping matches (keep the first one)
    const filteredMatches: any[] = []
    for (let i = 0; i < matches.length; i++) {
        const current = matches[i]
        const isOverlapping = filteredMatches.some((prev) => current.startIndex < prev.endIndex && current.endIndex > prev.startIndex)
        if (!isOverlapping) {
            filteredMatches.push(current)
        }
    }

    filteredMatches.forEach(({ match, style, startIndex }) => {
        // Add text before match
        if (startIndex > currentIndex) {
            const plainText = text.substring(currentIndex, startIndex)
            if (plainText) {
                runs.push(
                    new TextRun({
                        text: plainText,
                        color: "1F2937",
                        size: fontSize,
                    })
                )
            }
        }

        // Add styled text
        runs.push(
            new TextRun({
                text: match[1],
                color: style.font ? "374151" : "1F2937",
                size: fontSize,
                ...style,
            })
        )

        currentIndex = startIndex + match[0].length
    })

    // Add remaining text
    if (currentIndex < text.length) {
        const remainingText = text.substring(currentIndex)
        if (remainingText) {
            runs.push(
                new TextRun({
                    text: remainingText,
                    color: "1F2937",
                    size: fontSize,
                })
            )
        }
    }

    // If no formatting found, return simple text
    if (runs.length === 0) {
        runs.push(
            new TextRun({
                text: text,
                color: "1F2937",
                size: fontSize,
            })
        )
    }

    return runs
}
